import { useState } from "react";
import axios from "axios";
import { Mail, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { ASSETS, EDITABLE } from "@/data/content";
import SectionHeader from "./SectionHeader";
import Reveal from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLanguage();
  const c = t.contacto;
  const [form, setForm] = useState({ name: "", club: "", role: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(c.form.ok);
      setForm({ name: "", club: "", role: "", email: "", message: "" });
    } catch {
      toast.error(c.form.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-white/10 bg-[#0E131F] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-sky-400/60";

  return (
    <section id="contacto" className="mx-auto max-w-7xl scroll-mt-20 px-4 py-20 sm:px-6 sm:py-28 lg:px-8" data-testid="contacto-section">
      <SectionHeader number={`${c.number} · ${t.nav.contacto}`} title={c.title}>
        {c.intro}
      </SectionHeader>

      <div className="grid gap-8 lg:grid-cols-5">
        <Reveal className="lg:col-span-2">
          <article className="flex h-full flex-col rounded-2xl border border-white/10 bg-[#131927]/80 p-7 sm:p-8" data-testid="ikons-contact-card">
            <span className="inline-flex w-fit items-center rounded-lg bg-white px-3 py-2">
              <img src={ASSETS.logoIkons} alt="IKONS by Solocracks" className="h-7 w-auto" />
            </span>
            <p className="mt-6 font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-sky-400">
              {c.directCta}
            </p>
            <h3 className="mt-2 font-display text-xl font-bold uppercase tracking-wide text-white">
              {c.agentName}
            </h3>
            <p className="mt-1 text-sm text-slate-400">{c.agentRole}</p>
            <div className="mt-6 space-y-3">
              <a
                href={`mailto:${EDITABLE.contactEmail}`}
                data-testid="contact-email-link"
                className="flex items-center gap-3 text-sm text-slate-300 transition-colors hover:text-sky-300"
              >
                <Mail size={15} className="shrink-0 text-sky-400" />
                {EDITABLE.contactEmail}
              </a>
              <a
                href={EDITABLE.contactPhoneHref}
                data-testid="contact-phone-link"
                className="flex items-center gap-3 text-sm text-slate-300 transition-colors hover:text-sky-300"
              >
                <Phone size={15} className="shrink-0 text-sky-400" />
                {EDITABLE.contactPhone}
              </a>
            </div>
          </article>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-3">
          <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-[#0E131F] p-7 sm:p-8" data-testid="contact-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <input required value={form.name} onChange={update("name")} placeholder={c.form.name} className={inputCls} data-testid="contact-name-input" />
              <input value={form.club} onChange={update("club")} placeholder={c.form.club} className={inputCls} data-testid="contact-club-input" />
              <input value={form.role} onChange={update("role")} placeholder={c.form.role} className={inputCls} data-testid="contact-role-input" />
              <input required type="email" value={form.email} onChange={update("email")} placeholder={c.form.email} className={inputCls} data-testid="contact-email-input" />
            </div>
            <textarea
              required
              value={form.message}
              onChange={update("message")}
              placeholder={c.form.message}
              rows={5}
              className={`${inputCls} mt-4 resize-none`}
              data-testid="contact-message-input"
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-sky-400 px-7 py-3 text-xs font-bold uppercase tracking-[0.15em] text-[#07090E] transition-all duration-300 hover:bg-sky-300 hover:shadow-[0_0_30px_rgba(56,189,248,0.35)] disabled:opacity-50"
            >
              <Send size={14} />
              {sending ? c.form.sending : c.form.submit}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
